<template>
    <div v-if="canEdit">
        <div class="input-group mb-3">
            <input type="text" class="form-control" placeholder="OR NO." v-model="orNo">
            <input placeholder="Date" class="form-control" type="text" v-model="dateInput" @focus="setDateType"
                @blur="setTextType">
            <input type="text" class="form-control" placeholder="AMOUNT" v-model="amount">
            <input type="text" class="form-control" placeholder="Owner Address" v-model="ownerAddress">
        </div>

        <div class="input-group mb-3">
            <input type="text" class="form-control" placeholder="CORPORATION NAME" v-model="corporationName">
            <input type="text" class="form-control" placeholder="AREA OR LOCATION" v-model="area">
            <input type="text" class="form-control" placeholder="DECISION" v-model="decision">
            <input type="text" class="form-control" placeholder="RIGHT OVER LAND" v-model="rightOverLand">
        </div>

        <div class="input-group mb-3">
            <input type="text" class="form-control" placeholder="APPLICATION NO." v-model="applicationNo">
            <input type="text" class="form-control" placeholder="DECISION NO." v-model="decisionNo">
            <input placeholder="RECEIPT DATE" class="form-control" type="text" v-model="receiptDate"
                @focus="setDateType" @blur="setTextType">
            <input placeholder="DATE ISSUED" class="form-control" type="text" v-model="issuedDate" @focus="setDateType"
                @blur="setTextType">
        </div>
    </div>
    <div ref="certificateContainer" class="certificate-container">
        <!-- Decorative border -->
        <div class="certificate-border"></div>

        <!-- Optional watermark -->
        <div class="certificate-watermark">OFFICIAL DOCUMENT</div>

        <!-- Optional seal -->
        <div class="certificate-seal"></div>

        <div class="certificate-content">
            <TheSignature font-size="16px" max-img-height="100px" image-offset-y="-50"
                :position="JSON.parse(sig.signature_position_json)" :sig="sig" v-for="sig in signatories"
                :key="`sig${sig.official_id}`" />
            <!-- Header -->
            <div class="certificate-header">
                <img :src="mpdcImage" alt="MPDC Logo" class="header-logo" width="700">
                <br>
                <img v-if="qrCodeDataUrl" :src="qrCodeDataUrl" alt="QR Code" class="qr-code-image" width="120">
                <!-- Fallback to SOA image if no QR code -->
                <img v-else-if="currentSoaImage" :src="currentSoaImage" alt="SOA Image" class="sub-logo" width="200">
                <br><br>
                <b class="bin-number">BIN: {{ currentApplication?.application?.bin }}</b>
                <h5 class="document-title">LOCATIONAL CLEARANCE</h5>
            </div>

            <!-- Application and Decision Info -->
            <div class="row info-row">
                <div class="col-md-6 application-info">
                    APPLICATION: {{ applicationNo || 'N/A' }} <br />
                    DATE OF RECEIPT: {{ formatDate(receiptDate) }}
                </div>
                <div class="col-md-6 decision-info">
                    DECISION NO. {{ decisionNo || 'N/A' }} <br />
                    DATE ISSUED {{ formatDate(issuedDate) }}
                </div>
            </div>

            <!-- Information Table -->
            <div class="info-table-container">
                <table class="info-table">
                    <tbody>
                        <tr>
                            <td class="table-cell">
                                <b>NAME</b> <br />
                                {{ ownerFullName }}
                            </td>
                            <td class="table-cell">
                                <b>NAME OF CORPORATION</b> <br />
                                {{ corporationName || businessName }}
                            </td>
                        </tr>
                        <tr>
                            <td class="table-cell">
                                <b>ADDRESS</b> <br />
                                {{ ownerAddress?.toUpperCase() || 'N/A' }}
                            </td>
                            <td class="table-cell">
                                <b>ADDRESS</b> <br />
                                {{ businessAddress }}
                            </td>
                        </tr>
                        <tr>
                            <td class="table-cell">
                                <b>BUSINESS NAME</b> <br />
                                {{ businessName }}
                            </td>
                            <td class="table-cell">
                                <b>AREA AND LOCATION</b> <br />
                                {{ area || 'N/A' }}
                            </td>
                        </tr>
                        <tr>
                            <td class="table-cell">
                                <b>DECISION</b> <br />
                                {{ decision || 'N/A' }}
                            </td>
                            <td class="table-cell">
                                <b>RIGHT OVER LAND</b> <br />
                                {{ rightOverLand || 'N/A' }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Conditions Section -->
            <div class="conditions-section">
                <h4>CONDITIONS:</h4>
                <p class="conditions-text">
                    All conditions stipulated herein form part of this decision and are subject to monitoring.
                    <br /><br />
                    Non-compliance there with shall be a cause for cancellation or legal action.
                    <br /><br />
                    The applicable requirement of government agencies and applicable provisions of existing laws shall
                    be complied with.
                    <br /><br />
                    No activity other than that applied for shall be conducted within the project site.
                    <br /><br />
                    No major expansion, alteration and/or improvements shall be introduced without prior clearance from
                    this office.
                    <br /><br />
                    The decision shall not be construed as a certification of MPDC as to the ownership by the applicant
                    of the parcel of land subject of this decision
                    <br /><br />
                    Any misrepresentation, false statements or allegations material to the issuance of this decision
                    shall be sufficient cause for its revocation.
                </p>

                <h4>ADDITIONAL CONDITIONS:</h4>
                <p class="conditions-text">
                    Provisions as to the setbacks, yard requirements, bulk easement, area height and other restrictions
                    shall strictly conform with the requirements of the National Building Code and other related laws.
                    <br /><br />
                    This decision shall be considered automatically revoked if project is not commenced within one (1)
                    year from date of issue of this decision.
                    <br /><br />
                    Proper and regular disposal of wastes shall be strictly observed.
                    <br /><br />
                    Any complaint against the location and/or pollution of the project found valid after due hearing
                    shall be sufficient cause for the revocation of this permit.
                </p>
            </div>

            <!-- Footer -->
            <div class="document-footer">
                <div class="row">
                    <div class="col-md-8 payment-details">
                        <b>OR NO:</b> {{ orNo || 'N/A' }}<br>
                        <b>DATE:</b> {{ formatDate(dateInput) }}<br>
                        <b>AMOUNT:</b> {{ formattedAmount }}<br>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="action-buttons">
        <button v-if="canPrint" @click="printAsImage" class="btn btn-primary">
            Print Certificate
        </button>
        <button v-if="canPrint" @click="downloadAsImage" class="btn btn-success">
            Download Certificate
        </button>
        <button v-if="orNo && dateInput && amount && canSaveToIssue" class="btn btn-warning" @click="saveToIssue">
            SAVE TO ISSUE
        </button>
    </div>
</template>

<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import mpdcImage from '@/assets/img-headers/mpdc.png';
import html2canvas from 'html2canvas';
import QRCode from 'qrcode';
import signatureService from '@/services/signatureService';
import TheSignature from '../docs/TheSignature.vue';
import certificateService from '@/services/certificateService';
import confirmationService from '@/services/confirmationService';
import toastService from '@/services/toastService';
import spinnerService from '@/services/spinnerService';
import Formatters from '@/utils/formatters';
/* eslint-disable */
// Props
//eslint-disable-next-line
const props = defineProps({
    currentApplication: {
        type: Object,
        default: () => ({})
    },
    currentSoa: {
        type: Object,
        default: () => ({})
    },
    userSignatures: {
        type: Object,
        default: () => ({})
    },
    fontSize: {
        type: String,
        default: "14px"
    }
});

// Emits
//eslint-disable-next-line
const emit = defineEmits(['print', 'save']);

// Reactive data
const orNo = ref('');
const dateInput = ref('');
const amount = ref('');
const corporationName = ref('');
const area = ref('');
const decision = ref('');
const rightOverLand = ref('');
const applicationNo = ref('');
const decisionNo = ref('');
const receiptDate = ref('');
const issuedDate = ref('');
const ownerAddress = ref('');
const signatories = ref([]);
// const ownerAddress = computed(()=>{
//     const app = props.currentApplication;
//     const parts =[app.permitInfo?.owner_building_name,
//             app.permitInfo?.owner_block_no_lot_no,
//             app.permitInfo?.owner_street,
//         app.permitInfo?.owner_subdivision,
//         app.permitInfo?.owner_barangay,
//         app.permitInfo?.owner_city,
//         app.permitInfo?.owner_province
//     ].filter(part => part && part !== 'None' && part !== 'N/A' && part !== 'null');
//      return parts.join(' ') || 'N/A';
// });

const currentCertCode = ref("");
const canSaveToIssue = computed(() => {
    if (!props.currentApplication.application.application_id) return false;

    if (props.currentApplication.issuedCerts.findIndex(x => x.type === currentCertCode.value.toUpperCase()) === -1)
        return true;

    return false;
});

const canPrint = computed(() => {
    if (props.currentApplication.issuedCerts.findIndex(x => x.type === currentCertCode.value.toUpperCase()) > -1)
        return true;

    return false;
});


// Computed properties
const ownerFullName = computed(() => {
    const app = props.currentApplication;
    const firstName = app.permitInfo?.owner_firstname || '';
    const middleName = app.permitInfo?.owner_middlename || '';
    const lastName = app.permitInfo?.owner_lastname || '';

    // Format: FirstName [MiddleInitial.] LastName
    let fullName = firstName.trim();

    // Add middle initial if middle name exists and is not empty/null/NA
    if (middleName &&
        middleName.trim() !== '' &&
        middleName.trim().toLowerCase() !== 'n/a' &&
        middleName.trim().toLowerCase() !== 'null' &&
        middleName.trim().toLowerCase() !== 'none') {

        // Take first character of middle name and add period
        const middleInitial = middleName.trim().charAt(0).toUpperCase() + '.';
        fullName += ' ' + middleInitial;
    }

    // Add last name
    fullName += ' ' + lastName.trim();

    return fullName.trim();
});

const businessName = computed(() => {
    return props.currentApplication?.permitInfo?.business_name ||
        props.currentApplication?.permitInfo?.business_name ||
        'N/A';
});

const businessAddress = computed(() => {
    const app = props.currentApplication;
    const parts = [
        app.businessInfo?.operation_building_name,
        app.businessInfo?.operation_block_no_lot_no,
        app.businessInfo?.operation_street,
        app.businessInfo?.operation_subdivision,
        app.businessInfo?.operation_barangay,
        app.businessInfo?.operation_city,
        app.businessInfo?.operation_province
    ].filter(part => part && part !== 'None' && part !== 'N/A' && part !== 'null');
    return parts.join(' ') || 'N/A';
});

const currentSoaImage = computed(() => {
    if (props.currentSoa?.img_url) {
        if (props.currentSoa.img_url.startsWith('http') || props.currentSoa.img_url.startsWith('/')) {
            return props.currentSoa.img_url;
        }
        return `/uploads/${props.currentSoa.img_url}`;
    }
    return null;
});

const signaturePath = computed(() => {
    const sig = props.userSignatures?.LOC;
    if (sig?.ds_id && sig?.type) {
        return `ezgovtEngine/uploads/setup/signature/${sig.ds_id}/${sig.type}-sig.png`;
    }
    return null;
});

const formattedAmount = computed(() => {
    if (!amount.value) return '₱0.00';

    const num = parseFloat(amount.value);
    if (isNaN(num)) return amount.value;

    return new Intl.NumberFormat('en-PH', {
        style: 'currency',
        currency: 'PHP',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(num);
});

// Methods
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            const parts = dateString.split('/');
            if (parts.length === 3) {
                const [month, day, year] = parts;
                return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
            }
            return dateString;
        }

        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();

        return `${month}/${day}/${year}`;
    } catch (error) {
        return dateString;
    }
};

const setDateType = (event) => {
    event.target.type = 'date';
};

const setTextType = (event) => {
    if (!event.target.value) {
        event.target.type = 'text';
    }
};

const saveToIssue = async () => {
    const confirmed = await confirmationService.show("Are you sure you want to save and issue certificate?");
    if (!confirmed)
        return;
    const payload = {
        ref_date: dateInput.value,
        ref_no: orNo.value,
        type: 'LOC',
        amount: parseFloat(amount.value) || 0,
        period: props.currentApplication?.application?.year,
        application_id: props.currentApplication?.application.application_id,
        ownadd: ownerAddress.value,
        appno: applicationNo.value,
        decno: decisionNo.value,
        recdate: receiptDate.value,
        issdate: issuedDate.value,
        projtype: corporationName.value,
        rol: rightOverLand.value,
        decision: decision.value,
        area: area.value,
        remarks: ''
    };
    spinnerService.show();
    const resp = await certificateService.saveToIssue(payload);
    if (resp.success) {
        toastService.success("Clearance issued successfully!");
        emit("save-success");
    }
    else {
        toastService.error(resp.message);
    }
    spinnerService.hide();
    //  emit('save', );
};

// QR Code functionality
const qrCodeDataUrl = ref('');

const generateQRCodeData = () => {
    const app = props.currentApplication;
    if (!app || !app.application) return null;

    const qrData = {
        type: 'LOCATIONAL_CLEARANCE',
        bin: app.application.bin || '',
        applicationNo: applicationNo.value || '',
        decisionNo: decisionNo.value || '',
        businessName: businessName.value,
        ownerName: ownerFullName.value,
        issueDate: new Date().toISOString().split('T')[0],
        status: 'APPROVED'
    };

    return JSON.stringify(qrData);
};

const generateQRCode = async () => {
    try {
        const qrData = generateQRCodeData();

        if (!qrData) {
            qrCodeDataUrl.value = '';
            return;
        }

        const url = await QRCode.toDataURL(qrData, {
            width: 120,
            margin: 2,
            color: {
                dark: '#000000',
                light: '#FFFFFF'
            },
            errorCorrectionLevel: 'H'
        });

        qrCodeDataUrl.value = url;

    } catch (error) {
        console.error('Error generating QR code:', error);
        qrCodeDataUrl.value = '';
    }
};

// Printing functionality
const certificateContainer = ref(null);

const printAsImage = async () => {
    if (!certificateContainer.value) return;

    try {
        const loading = showLoading('Generating certificate image...');

        const canvas = await html2canvas(certificateContainer.value, {
            scale: 2,
            backgroundColor: '#ffffff',
            useCORS: true,
            allowTaint: true,
            logging: false,
            onclone: (clonedDoc) => {
                const images = clonedDoc.querySelectorAll('img');
                return Promise.all(
                    Array.from(images).map(img => {
                        if (img.complete) return Promise.resolve();
                        return new Promise((resolve, reject) => {
                            img.onload = resolve;
                            img.onerror = reject;
                        });
                    })
                );
            }
        });

        const imageData = canvas.toDataURL('image/png', 1.0);
        await printImageData(imageData);
        loading.hide();
    } catch (error) {
        console.error('Error generating certificate image:', error);
        alert('Failed to generate certificate. Please try again.');
    }
};

const downloadAsImage = async () => {
    if (!certificateContainer.value) return;

    try {
        const canvas = await html2canvas(certificateContainer.value, {
            scale: 2,
            backgroundColor: '#ffffff',
            useCORS: true
        });

        const link = document.createElement('a');
        link.download = `locational-clearance-${new Date().getTime()}.png`;
        link.href = canvas.toDataURL('image/png', 1.0);
        link.click();
    } catch (error) {
        console.error('Error downloading certificate:', error);
    }
};

const printImageData = (imageData) => {
    return new Promise((resolve) => {
        const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Print Certificate</title>
        <style>
          @page { margin: 0; }
          body { margin: 0; }
          img { width: 100%; height: auto; }
        </style>
      </head>
      <body>
        <img src="${imageData}" 
             onload="setTimeout(() => { window.print(); setTimeout(() => window.close(), 1000); }, 500);"
             onerror="window.close()">
        <script>
          window.onafterprint = () => setTimeout(() => window.close(), 500);
          setTimeout(() => { if(!window.closed) window.close(); }, 10000);
        <\/script>
      </body>
      </html>
    `;

        const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
        const htmlUrl = URL.createObjectURL(htmlBlob);

        const newTab = window.open(htmlUrl, '_blank');

        if (!newTab) {
            const printIframe = document.createElement('iframe');
            printIframe.style.cssText = 'position:fixed;width:0;height:0;border:none;';
            printIframe.srcdoc = htmlContent;
            document.body.appendChild(printIframe);

            printIframe.onload = () => {
                setTimeout(() => {
                    printIframe.contentWindow.print();
                    setTimeout(() => {
                        document.body.removeChild(printIframe);
                        URL.revokeObjectURL(htmlUrl);
                    }, 1000);
                }, 500);
            };
        } else {
            setTimeout(() => URL.revokeObjectURL(htmlUrl), 1000);
        }

        resolve();
    });
};

const showLoading = (message) => {
    const loading = document.createElement('div');
    loading.innerHTML = `
    <div style="
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0,0,0,0.7);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 9999;
      color: white;
      font-size: 18px;
      flex-direction: column;
    ">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <div style="margin-top: 20px">${message}</div>
    </div>
  `;
    document.body.appendChild(loading);

    return {
        hide: () => {
            if (loading.parentNode) {
                loading.parentNode.removeChild(loading);
            }
        }
    };
};

const preProcessCertInfo = (certInfo) => {
    if (!certInfo) return;
    orNo.value = certInfo.ref_no;
    dateInput.value = Formatters.formatDateForInput(certInfo.ref_date);
    amount.value = certInfo.amount;
    ownerAddress.value = certInfo.ownadd;
    applicationNo.value = certInfo.appno;
    decisionNo.value = certInfo.decno;
    receiptDate.value = certInfo.recdate;
    issuedDate.value = Formatters.formatDateForInput(certInfo.issdate);
    corporationName.value = certInfo.projtype;
    rightOverLand.value = certInfo.rol;
    decision.value = certInfo.decision;
    area.value = certInfo.area;
}

const getSignatories = async (code) => {
    currentCertCode.value = code;
    const certInfo = props.currentApplication.issuedCerts.find(x => x.type === code.toUpperCase());
    preProcessCertInfo(certInfo);
    signatories.value = await signatureService.getSignatoriesByCode(code);
}
defineExpose({ getSignatories });

watch(() => props.fontSize, (newVal) => {
    alert(newVal)
});

// Initialize QR code and watch for changes
onMounted(() => {
    generateQRCode();
});

watch(() => props.currentApplication, (newApp) => {
    if (!newApp || !newApp.application) {
        orNo.value = '';
        dateInput.value = '';
        amount.value = '';
        ownerAddress.value = '';
        corporationName.value = '';
        area.value = '';
        decision.value = '';
        rightOverLand.value = '';
        applicationNo.value = '';
        decisionNo.value = '';
        receiptDate.value = '';
        issuedDate.value = '';
    }
    generateQRCode();
}, { deep: true });
</script>

<style scoped>
/* Certificate container - fixed width for all screens */
.certificate-container {
    background-color: white;
    padding: 5px 40px;
    width: 1000px;
    margin: 0 auto;
    min-height: 1400px;
    box-sizing: border-box;
    position: relative;
    page-break-inside: avoid;
}

/* Certificate content area */
.certificate-content {
    width: 100%;
    height: 100%;
    position: relative;
}

/* Certificate border decoration */
.certificate-border {
    position: absolute;
    top: 20px;
    left: 20px;
    right: 20px;
    bottom: 20px;
    border: 3px double #1c6bb7;
    pointer-events: none;
    z-index: 1;
}

/* Header section */
.certificate-header {
    text-align: center;
    margin-bottom: 10px;
    padding-bottom: 10px;
    border-bottom: 2px solid #1c6bb7;
    position: relative;
    z-index: 2;
}

.header-logo {
    max-width: 700px;
    width: 100%;
    height: auto;
    margin: 0 auto 0px;
    display: block;
}

.sub-logo {
    width: 200px;
    height: auto;
    margin: 0px auto;
    display: block;
}

.document-title {
    font-family: 'Times New Roman', Times, serif;
    color: darkblue;
    font-weight: bold;
    margin: 5px 0;
    font-size: 24px;
    letter-spacing: 2px;
    text-transform: uppercase;
}

.bin-number {
    font-family: 'Times New Roman', Times, serif;
    font-size: 16px;
    font-weight: bold;
    color: #333;
    display: block;
    margin: 5px 0;
}

/* Info Row */
.info-row {
    margin: 20px 0;
    padding: 0 50px;
}

.application-info,
.decision-info {
    text-align: center;
    font-family: 'Times New Roman', Times, serif;
    font-size: 16px;
    color: #222;
    line-height: 1.6;
}

/* Info Table */
.info-table-container {
    padding: 0 50px;
    margin: 5px 0;
}

.info-table {
    width: 100%;
    border: 1px solid #000;
    border-collapse: collapse;
    font-family: 'Times New Roman', Times, serif;
    font-size: 14px;
    color: #000;
}

.table-cell {
    border: 1px solid #000;
    padding: 5px;
    vertical-align: top;
    line-height: 1.15;
}

/* Conditions Section */
.conditions-section {
    padding: 0 50px;
    margin: 30px 0;
}

.conditions-section h4 {
    font-family: 'Times New Roman', Times, serif;
    color: darkblue;
    font-weight: bold;
    margin: 20px 0 10px;
}

.conditions-text {
    font-family: 'Times New Roman', Times, serif;
    font-size: 16px;
    color: #222;
    line-height: 0.90;
    text-align: justify;
    margin-bottom: 15px;
}

/* Document footer */
.document-footer {
    margin-top: 10px;
    padding: 0 50px;
    position: relative;
    z-index: 2;
}

.payment-details {
    color: #222;
    font-size: 16px;
    line-height: 2;
    font-family: 'Times New Roman', Times, serif;
    margin-bottom: 20px;
}

.signature-section {
    position: relative;
    min-height: 200px;
}

.signature-container {
    position: relative;
    text-align: center;
    padding-top: 80px;
}

.signature-line {
    width: 300px;
    height: 1px;
    background-color: #333;
    margin: 0 auto 5px;
}

.signature-image {
    position: absolute;
    top: 10px;
    left: 50%;
    transform: translateX(-50%);
    width: 150px;
    height: auto;
    opacity: 0.9;
    z-index: 1;
}

.signatory-name {
    font-family: 'Times New Roman', Times, serif;
    font-size: 17px;
    font-weight: bold;
    color: #222;
    margin-top: 10px;
}

.signatory-title {
    font-family: 'Times New Roman', Times, serif;
    font-size: 16px;
    color: #555;
    margin-top: 5px;
}

/* QR Code */
.qr-code-image {
    border: 1px solid #ddd;
    padding: 5px;
    background: white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    margin: 0px 0;
}

/* Input controls */
.input-group {
    gap: 10px;
    margin-bottom: 20px;
    max-width: 1000px;
    margin-left: auto;
    margin-right: auto;
}

.input-group .form-control {
    flex: 1;
    min-width: 0;
    font-size: 14px;
    padding: 8px 12px;
}

.action-buttons {
    text-align: center;
    margin-top: 20px;
    margin-bottom: 30px;
}

.action-buttons .btn {
    margin: 0 5px;
}

/* Watermark style */
.certificate-watermark {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(-45deg);
    font-size: 80px;
    color: rgba(28, 107, 183, 0.05);
    font-weight: bold;
    white-space: nowrap;
    z-index: 0;
    pointer-events: none;
    user-select: none;
}

/* Seal/stamp effect */
.certificate-seal {
    position: absolute;
    bottom: 100px;
    right: 100px;
    width: 150px;
    height: 150px;
    border: 3px solid #1c6bb7;
    border-radius: 50%;
    opacity: 0.1;
    z-index: 0;
    pointer-events: none;
}

/* PRINT-ONLY STYLES */
@media print {
    @page {
        size: A4 portrait;
        margin: 0;
    }

    body,
    html {
        margin: 0;
        padding: 0;
        background: white !important;
        width: 210mm;
        min-height: 297mm;
    }

    .certificate-container {
        width: 100% !important;
        max-width: 100% !important;
        min-height: 297mm !important;
        margin: 0 !important;
        padding: 20mm !important;
        border: none !important;
        box-shadow: none !important;
        page-break-after: always;
        break-inside: avoid;
    }

    body * {
        visibility: hidden;
    }

    .certificate-container,
    .certificate-container * {
        visibility: visible;
    }

    .certificate-container {
        position: absolute;
        left: 0;
        top: 0;
        right: 0;
        bottom: 0;
    }

    .input-group,
    .action-buttons,
    .btn,
    button {
        display: none !important;
    }

    .certificate-border {
        border: 3px double #1c6bb7 !important;
    }

    .header-logo {
        width: 140mm !important;
        max-width: 90% !important;
    }

    .document-title,
    .conditions-text,
    .payment-details,
    .signatory-name,
    .signatory-title,
    .bin-number,
    .application-info,
    .decision-info,
    .table-cell {
        color: #000 !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
    }

    .conditions-text {
        font-size: 14pt !important;
        line-height: 1.6 !important;
    }

    .certificate-header,
    .conditions-section,
    .document-footer {
        page-break-inside: avoid;
        break-inside: avoid;
    }

    .conditions-section {
        padding: 0 30px !important;
    }

    .document-footer {
        margin-top: 40px !important;
        padding: 0 30px !important;
    }

    .signature-image {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
    }
}

/* SCREEN-ONLY STYLES */
@media screen {
    .certificate-container {
        overflow: auto;
        max-height: 90vh;
    }

    .certificate-container::-webkit-scrollbar {
        width: 8px;
        height: 8px;
    }

    .certificate-container::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 4px;
    }

    .certificate-container::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 4px;
    }

    .certificate-container::-webkit-scrollbar-thumb:hover {
        background: #555;
    }
}

@media print and (color) {
    * {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
    }
}
</style>